import { ReactNode } from 'react';
import styled from 'styled-components';
import { BouColor } from '../../../global/bou-colors';
import RadioButtonSvg from './radio-button.svg';
import { RadioButtonCommonProps } from './types';

export interface Props extends RadioButtonCommonProps {
  checked: boolean;
  onSelect: () => void;
  onClick?: () => void;
  label?: ReactNode;
}

export const BouRadioButtonWithLabel = (props: Props) => {
  const onClick = () => {
    onInvisibleChange();
    if (props.onClick) {
      props.onClick();
    }
  };

  const onInvisibleChange = () => {
    if (!props.disabled) {
      props.onSelect();
    }
  };

  const radioSize = props.radioSize || '1.5em';

  const color = props.invalid ? BouColor.TOOL_TEXT_ERROR_RED : props.color;
  const hoverColor = props.invalid ? BouColor.TOOL_TEXT_ERROR_RED : props.hoverColor;

  return (
    <Root onClick={onClick} className={props.className} disabled={props.disabled}>
      <InvisibleRadioButton
        id={props.id}
        checked={props.checked}
        onChange={onInvisibleChange}
        radioSize={radioSize}
        disabled={props.disabled}
      />
      <RadioButton
        checked={props.checked ? 1 : 0}
        graywhenunchecked={props.grayWhenUnchecked ? 1 : 0}
        disabled={props.disabled ? 1 : 0}
        radiosize={radioSize}
        disablehover={props.disableHover ? 1 : 0}
        color={color}
        hovercolor={hoverColor}
        backgroundfillcolor={props.backgroundfillcolor}
      />
      {props.label && (
        <RadioLabel
          htmlFor={props.id}
          checked={props.checked}
          grayWhenUnchecked={props.grayWhenUnchecked}
          disabled={props.disabled}
          disableHover={props.disableHover}
          color={color}
          hoverColor={hoverColor}
        >
          {props.label}
        </RadioLabel>
      )}
    </Root>
  );
};

const Root = styled.div<{ disabled?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  cursor: ${props => !props.disabled && 'pointer'};
`;

const RadioButton = styled(RadioButtonSvg)<{
  checked: number;
  graywhenunchecked: number;
  disablehover: number;
  disabled: number;
  radiosize: string;
  color?: string;
  hovercolor?: string;
  backgroundfillcolor?: string;
}>`
  overflow: visible;
  width: ${props => props.radiosize};
  height: ${props => props.radiosize};
  flex-shrink: 0;
  cursor: ${props => !props.disabled && 'pointer'};
  fill: ${props => (props.backgroundfillcolor && !props.disabled ? props.backgroundfillcolor : 'transparent')};
  .radio-button_svg__inner,
  .inner {
    fill: ${props =>
      getColor({
        checked: !!props.checked,
        grayWhenUnchecked: false,
        disabled: !!props.disabled,
        hovered: false,
        transparentWhenNotSelected: true,
        color: props.color,
        hoverColor: props.hovercolor,
      })};
    color: ${props => props.disabled && BouColor.PLATFORM_DISABLED_GRAY};
    ${Root}:hover & {
      fill: ${props =>
        !props.disablehover &&
        getColor({
          checked: !!props.checked,
          grayWhenUnchecked: false,
          disabled: !!props.disabled,
          hovered: true,
          transparentWhenNotSelected: true,
          color: props.color,
          hoverColor: props.hovercolor,
        })};
    }
    cursor: ${props => !props.disabled && 'pointer'};
  }
  .radio-button_svg__outer,
  .outer {
    stroke: ${props =>
      getColor({
        checked: !!props.checked,
        grayWhenUnchecked: !!props.graywhenunchecked,
        disabled: !!props.disabled,
        color: props.color,
        hoverColor: props.hovercolor,
      })};
    ${Root}:hover & {
      stroke: ${props =>
        !props.disablehover &&
        getColor({
          checked: !!props.checked,
          grayWhenUnchecked: !!props.graywhenunchecked,
          disabled: !!props.disabled,
          hovered: true,
          color: props.color,
          hoverColor: props.hovercolor,
        })};
    }
    cursor: ${props => !props.disabled && 'pointer'};
  }
`;

// For accessibility stuffz
const InvisibleRadioButton = styled.input.attrs({ type: 'radio' })<{ radioSize: string }>`
  width: ${props => props.radioSize};
  height: ${props => props.radioSize};
  opacity: 0;
  flex-shrink: 0;
  position: absolute;
  margin: 0;
  cursor: ${props => !props.disabled && 'pointer'};
`;

const RadioLabel = styled.label<{
  checked: boolean;
  grayWhenUnchecked?: boolean;
  disabled?: boolean;
  disableHover?: boolean;
  color?: string;
  hoverColor?: string;
}>`
  padding-left: 0.8em;
  cursor: ${props => !props.disabled && 'pointer'};
  color: ${props =>
    getColor({
      checked: props.checked,
      grayWhenUnchecked: props.grayWhenUnchecked,
      disabled: props.disabled,
      color: props.color,
      hoverColor: props.hoverColor,
    })};
  ${Root}:hover & {
    color: ${props =>
      !props.disableHover &&
      getColor({
        checked: props.checked,
        grayWhenUnchecked: props.grayWhenUnchecked,
        disabled: props.disabled,
        hovered: true,
        color: props.color,
        hoverColor: props.hoverColor,
      })};
  }
`;

function getColor(options: {
  checked: boolean;
  grayWhenUnchecked?: boolean;
  disabled?: boolean;
  hovered?: boolean;
  transparentWhenNotSelected?: boolean;
  color?: string;
  hoverColor?: string;
}) {
  const { checked, grayWhenUnchecked, disabled, hovered, transparentWhenNotSelected } = options;

  const defaultColor = options.color || BouColor.PLATFORM_TEXT_BLACK;
  const hoverColor = options.hoverColor || BouColor.PLATFORM_TEXT_GRAY_LIGHT;
  const disabledColor = BouColor.PLATFORM_DISABLED_GRAY;

  if (disabled) {
    if (transparentWhenNotSelected) {
      return 'transparent';
    }
    return disabledColor;
  }
  if (hovered) {
    if (transparentWhenNotSelected && !checked) {
      return 'transparent';
    }
    if (grayWhenUnchecked) {
      return defaultColor;
    }
    return hoverColor;
  }
  if (checked) {
    return defaultColor;
  }
  if (transparentWhenNotSelected) {
    return 'transparent';
  }
  if (grayWhenUnchecked) {
    return BouColor.PLATFORM_TEXT_GRAY_LIGHT;
  }
  return defaultColor;
}
